import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { forgotPassword } from '../auth/cognito';
import {
  AuthLayout,
  RESET_REQUESTED_NOTICE,
  describedBy,
  readAuthState,
  type AuthRouteState,
} from '../components/AuthLayout';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const arrival = readAuthState(location.state);

  const [email, setEmail] = useState(arrival.email ?? '');
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    const address = email.trim();
    try {
      await forgotPassword(address);
    } catch {
      // Swallowed deliberately, and this is the whole point of the page.
      //
      // The user pool client sets preventUserExistenceErrors, so Cognito
      // answers a reset request for an unknown address exactly as it answers
      // one for a real account. If this form reacted to the difference — a
      // visible error, a slower path, a page that stays put — it would hand
      // back the account-enumeration oracle the pool setting exists to remove,
      // and an attacker could sift a leaked address list against it.
      //
      // So every outcome lands in the same place with the same sentence. The
      // cost is that a genuine failure (throttling, a network drop) looks like
      // success here; the reset page carries a "send a new code" button so that
      // case is recoverable, and any real bug is visible in the browser console
      // rather than in a message that would also describe someone's account.
    }

    const state: AuthRouteState = { email: address, notice: RESET_REQUESTED_NOTICE };
    navigate('/reset-password', { state });
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a code to set a new one."
      footer={
        <>
          Remembered it? <Link className="link" to="/login">Back to sign in</Link>
        </>
      }
    >
      <form className="stack" onSubmit={onSubmit} aria-busy={busy}>
        <fieldset className="fieldset" disabled={busy}>
          <div className="field">
            <label className="label" htmlFor="forgot-email">
              Email
            </label>
            <input
              id="forgot-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-describedby={describedBy('forgot-email-hint')}
            />
            <p className="hint" id="forgot-email-hint">
              Use the address you signed up with.
            </p>
          </div>

          <button type="submit" className="btn btn--primary btn--block">
            {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
            {busy ? 'Sending…' : 'Send reset code'}
          </button>
        </fieldset>
      </form>
    </AuthLayout>
  );
}
